import { checkPhone, checkSmsCode, getHostbaseUrl } from '@core/tools';
import { bankTypeLabel, EBankType, ECompanyType, EPayeeUserType, IAccountOpening, IBankApplication, IFinanceLianBankBind } from '@core/main/types';
import { ComputedRef, Ref } from 'vue';
import { ISecurePassword } from '@vue3/components';
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';

const hostUrl = getHostbaseUrl();

export const fromTypeValues = {
  payer: 'payer',
  payee: 'payee',
  lianlian: 'agent',
};

export function getFormRules({
  formValues,
  passObj,
}: {
  formValues: Ref<IAccountOpening>;
  passObj: Ref<ISecurePassword>;
}) {
  return {
    bankType: [
      {
        required: true,
        message: '请选择账户类型',
      },
    ],
    bankAccountName: [
      {
        required: true,
        message: '请输入账户名',
      },
    ],

    bankCode: [
      {
        required: true,
        message: '请选择企业开户银行',
      },
    ],
    bankNo: [
      {
        required: true,
        message: '请输入企业基本账号',
      },
    ],
    cityCode: [
      {
        required: true,
        message: '请选择开户城市所在省市',
      },
    ],
    bankBranchNo: [
      {
        required: true,
        message: '输入关键字搜索并选择开户支行',
      },
    ],
    customBankBranchName: [
      {
        required: true,
        message: '请输入开户支行名称',
        trigger: 'blur',
      },
    ],
    cnapsCode: [
      {
        required: true,
        message: '请输入大额行号',
      },
    ],
    payPassword: [
      {
        required: true,
        message: '请输入支付密码',
      },
      {
        required: true,
        validator: (rule: any, value: string, callback: any) => {
          if (passObj.value.hash && passObj.value.length < 6) {
            callback(new Error('支付密码必须是6-20位'));
          }
          callback();
        },
      },

    ],
    phoneNo: [
      {
        required: true,
        validator: (rule: any, value: string, callback: any) => {
          if (checkPhone(formValues.value.phoneNo, '银行预留')) {
            callback(checkPhone(formValues.value.phoneNo, '银行预留'));
          }
          callback();
        },
      },

    ],
    code: [
      {
        required: true,
        validator: (rule: any, value: string, callback: any) => {
          if (checkSmsCode(String(formValues.value.code))) {
            callback(checkSmsCode(String(formValues.value.code)));
          }
          callback();
        },
      },

    ],
  };
}

export function getBankTypeOptions({
  from,
  type,
  bankType,
  payeeType,
}: {
  from: ComputedRef<'payer' | 'payee' | 'lianlian'>;
  type: ECompanyType;
  payeeType: EPayeeUserType;
  bankType: EBankType;
}): Array<{label: string; value: EBankType}> {
  if (from.value === 'payer') { // 付款方
    if (type === ECompanyType.Personal) {
      return [
        {
          label: bankTypeLabel[EBankType.ENTERPRISE],
          value: EBankType.ENTERPRISE,
        },
        {
          label: bankTypeLabel[EBankType.LEGALREPT],
          value: EBankType.LEGALREPT,
        },
      ];
    }

    return [
      {
        label: bankTypeLabel[EBankType.ENTERPRISE],
        value: EBankType.ENTERPRISE,
      },
    ];
  }

  if (from.value === 'payee') { // 收款方
    if (payeeType === EPayeeUserType.Personal) {
      return [
        {
          label: bankTypeLabel[EBankType.PERSONAL],
          value: EBankType.PERSONAL,
        },
      ];
    }

    return [
      {
        label: bankTypeLabel[EBankType.ENTERPRISE],
        value: EBankType.ENTERPRISE,
      },
    ];
  }

  if (from.value === 'lianlian') { // 代理商
    if (type === ECompanyType.Personal) {
      return [
        {
          label: bankTypeLabel[EBankType.ENTERPRISE],
          value: EBankType.ENTERPRISE,
        },
        {
          label: bankTypeLabel[EBankType.LEGALREPT],
          value: EBankType.LEGALREPT,
        },
      ];
    }

    if (bankType === EBankType.PERSONAL) {
      return [
        {
          label: bankTypeLabel[EBankType.PERSONAL],
          value: EBankType.PERSONAL,
        },
      ];
    }

    if (bankType === EBankType.LEGALREPT) {
      return [
        {
          label: bankTypeLabel[EBankType.LEGALREPT],
          value: EBankType.LEGALREPT,
        },
      ];
    }

    return [
      {
        label: bankTypeLabel[EBankType.ENTERPRISE],
        value: EBankType.ENTERPRISE,
      },
    ];
  }

  return [];
}

/**
 * 获取绑定银行卡提交参数
 * @param module 功能模块（normal: 通用模块(付款方，收款方)，finance: 财务管理中的连连）
 */
export function getBindBankSubmitParams({
  formValues,
  lianId,
  payPassword,
  randomKey,
  module = 'normal',
}: {
  lianId: number;
  payPassword: string;
  randomKey: string;
  module?: 'normal' | 'finance';
  formValues: Ref<IAccountOpening>;
}) {
  const {
    bankAccountName,
    bankNo,
    bankCode,
    brabankName,
    provinceCode,
    cityCode,
    customStatus,
    bankBranchNo,
    relationBranchBankName,
    cnapsCode,
    customBankBranchName,
    bankType,
    phoneNo,
  } = formValues.value;

  const isPersonal = bankType === EBankType.PERSONAL || bankType === EBankType.LEGALREPT;

  if (module === 'finance') {
    return {
      lianId,
      bankType,
      bankAccountName,
      bankNo,
      bankCode,
      bankName: brabankName, // different from normal
      customStatus,
      password: payPassword, // different from normal
      randomKey,
      linkedPhone: phoneNo, // different from normal
      cityCode: isPersonal ? undefined : cityCode,
      provinceCode: isPersonal ? undefined : provinceCode,
      cnapsCode: isPersonal ? undefined : customStatus === 0 ? bankBranchNo : cnapsCode,
      bankBranch: isPersonal ? undefined : customStatus === 0 ? relationBranchBankName : customBankBranchName,
    } as IFinanceLianBankBind;
  }

  return {
    lianId,
    bankType,
    bankAccountName,
    bankNo,
    bankCode,
    brabankName,
    customStatus,
    payPassword,
    randomKey,
    phoneNo,
    cityCode: isPersonal ? undefined : cityCode,
    provinceCode: isPersonal ? undefined : provinceCode,
    cnapsCode: isPersonal ? undefined : customStatus === 0 ? bankBranchNo : cnapsCode,
    bankBranch: isPersonal ? undefined : customStatus === 0 ? relationBranchBankName : customBankBranchName,
  } as IBankApplication;
}

/**
 * lianlian - 更换银行卡申请
 */
export async function lianlianBankBindSubmit({
  params,
  type,
  module = 'normal',
}: {
  params: IBankApplication | IFinanceLianBankBind;
  type: 'payer' | 'payee' | 'agent';
  module?: 'normal' | 'finance';
}) {
  let data = {} as ILianBankBindSubmitRes;
  const prefix = module === 'finance' ? 'unified-lian-account/manage' : `${type}/bank`;

  try {
    const { code, context, message } = await Http.post<ILianBankBindSubmitRes>(
      `${hostUrl}/pay/${prefix}/change-card-apply`,
      JSON.parse(JSON.stringify(params)),
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as ILianBankBindSubmitRes;

      if (module === 'finance') {
        data = {
          token: res.token,
          txnSeqno: res.txnSeqno || res.txn_seqno,
          linkedAgrtno: res.linkedAgrtno,
        };
      } else {
        data = {
          txn_seqno: res.txn_seqno,
          token: res.token,
        };
      }
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * lianlian - 更换银行卡验证
 */
export async function lianlianBankBindVerify({
  params,
  type,
  module = 'normal',
}: {
  params: ILianBankBindVerifyParam;
  type: 'payer' | 'payee' | 'agent';
  module?: 'normal' | 'finance';
}) {
  const prefix = module === 'finance' ? 'unified-lian-account/manage' : `${type}/bank`;

  try {
    const { code, message } = await Http.post(
      `${hostUrl}/pay/${prefix}/change-card-verify`,
      {
        ...JSON.parse(JSON.stringify(params)),
        linkedAgrtno: module === 'finance' ? params.linkedAgrtno : undefined,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {

      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

export interface ILianBankBindSubmitRes {
  /** 商户系统唯一交易流水号 */
  txn_seqno?: number;
  txnSeqno?: number;
  /** 连连协议号 */
  linkedAgrtno?: string;
  /** 	授权令牌 */
  token: string;
}

export interface ILianBankBindVerifyParam {
  /** 连连Id */
  lianId: number;
  /** 令牌 */
  token: string;
  /** 验证码 */
  verifyCode: string;
  /** 连连协议号 */
  linkedAgrtno?: string;
}

export interface ILianBankBindExtraParam {
  from: 'payer' | 'payee' | 'lianlian';
  module: 'normal' | 'finance';
  randomApiMethod: 'get' | 'post';
  randomApiPath: string;
}
